import { parseFirst, Statement, toSql } from 'pgsql-ast-parser/lib';
import { GraphQlRequestService } from '../../../api/graphql-request-service';
import {
  Months,
  TimeSliderState,
} from '../../common/components/timeslider/types';
import { Domains, termDateNormalizedField } from '../../constants';
import {
  DataFieldWithDataType,
  DataTypes,
  EmployeeDataFields,
} from '../../types';
import { Hasher } from '../hasher';
import { startDateFromTimePointType } from '../timeranges/utils';
import { HasDependencies, hashString, Role, VersionId } from '../types';
import { handleGQLErrors, parseCustomSqlValue } from '../utils';
import {
  CustomSqlQueryValueBackendType,
  EMPLOYMENT_TEMPORALITY,
  Where,
} from './types';
import {
  dataTypeToExpr,
  employmentTemporalitySql,
  fieldToSql,
  nonHierarchicalfieldToExpr,
  timeSliderStateToExpr,
} from './utils';
import { SQL } from './utils-sql';

export interface DataService extends HasDependencies {
  queryDataFieldValues: (
    dimensions: DataFieldWithDataType[],
    timeSliderState?: TimeSliderState,
    firstMonthOfYear?: Months,
    range?: boolean,
    where?: Where
  ) => Promise<CustomSqlQueryValueBackendType[][] | undefined>;

  getOldestStartDate: (latestVersionId: VersionId) => Promise<VersionId>;
}

export class BackendDataService implements DataService {
  constructor(
    readonly domain: Domains,
    readonly graphQlRequestService: GraphQlRequestService,
    readonly executorRole: Role,
    readonly simulatedRole: Role | null
  ) {}

  public getDeps = () => [
    hashString([
      this.domain,
      ...this.graphQlRequestService.getDeps(),
      Hasher.hash(this.executorRole),
      Hasher.hash(this.simulatedRole),
    ]),
  ];

  public queryDataFieldValues = async (
    dimensions: DataFieldWithDataType[],
    timeSliderState?: TimeSliderState,
    firstMonthOfYear?: Months,
    range?: boolean,
    where?: Where
  ): Promise<CustomSqlQueryValueBackendType[][] | undefined> => {
    const dataType = dimensions[0].dataType;
    const selection = dimensions
      .map((dimension) => fieldToSql(dimension))
      .join(',');
    const timeSelection = timeSliderState
      ? toSql.expr(timeSliderStateToExpr(timeSliderState, range, dataType))
      : null;
    const employmentTemporalitySelection =
      dataType === DataTypes.EMPLOYEE
        ? employmentTemporalitySql([
            EMPLOYMENT_TEMPORALITY.PRESENT,
            EMPLOYMENT_TEMPORALITY.PAST,
          ])
        : null;
    const termDateSelection =
      dataType === DataTypes.EMPLOYEE && timeSliderState && firstMonthOfYear
        ? `${fieldToSql(termDateNormalizedField)} >= '${startDateFromTimePointType(
            timeSliderState.start,
            timeSliderState.selectedGranularity,
            firstMonthOfYear
          )}'`
        : null;
    const statementAsString = SQL.queryToString({
      select: selection,
      from: toSql.expr(dataTypeToExpr(dataType)),
      where: [
        where ?? null,
        timeSelection,
        employmentTemporalitySelection,
        termDateSelection,
      ].flatMap((i) => (i ? [i] : [])),
      groupBy: selection,
    });

    const statement: Statement = parseFirst(statementAsString);
    const query = toSql.statement(statement);

    const result = await handleGQLErrors(
      this.graphQlRequestService.graphQlSdk.executeCustomSqlQuery({
        domain: this.domain,
        querySql: query,
        selectedExecutorRole: this.executorRole.id,
        simulateRole: this.simulatedRole?.id ?? null,
        disableNestLoop: true,
      })
    );
    const data = result.executeCustomSqlQuery?.data;
    return data;
  };

  public getOldestStartDate = async (
    latestVersionId: VersionId
  ): Promise<VersionId> => {
    const startDate = toSql.expr(
      nonHierarchicalfieldToExpr({
        dataType: DataTypes.EMPLOYEE,
        dataField: EmployeeDataFields.START_DATE,
      })
    );
    const versionId = toSql.expr(
      nonHierarchicalfieldToExpr({
        dataType: DataTypes.EMPLOYEE,
        dataField: EmployeeDataFields.VERSION_ID,
      })
    );
    const employmentTemporalitySelection = employmentTemporalitySql([
      EMPLOYMENT_TEMPORALITY.PRESENT,
      EMPLOYMENT_TEMPORALITY.PAST,
    ]);

    const versionIdSelection = `${versionId} = '${latestVersionId}'`;

    const statementAsString = SQL.queryToString({
      select: `MIN(${startDate})`,
      from: toSql.expr(dataTypeToExpr(DataTypes.EMPLOYEE)),
      where: [employmentTemporalitySelection, versionIdSelection],
    });
    const statement: Statement = parseFirst(statementAsString);
    const query = toSql.statement(statement);

    const result = await handleGQLErrors(
      this.graphQlRequestService.graphQlSdk.executeCustomSqlQuery({
        domain: this.domain,
        querySql: query,
        selectedExecutorRole: this.executorRole.id,
        simulateRole: this.simulatedRole?.id ?? null,
        disableNestLoop: true,
      })
    );
    const sqlData = result.executeCustomSqlQuery?.data[0][0];
    const data = sqlData ? parseCustomSqlValue(sqlData) : null;
    return data as string;
  };
}
